/**
 * Command Suggestions Hook
 *
 * Suggestion'ları filtreler, sıralar, dynamic content loader
 * History-based ranking
 */

import { useState, useEffect, useMemo } from "react";
import { useSlashCommandStore } from "../store/slash-command-store";
import { useCommandParser } from "./use-command-parser";
import { findCommandByPath } from "../registry/command-registry";
import type { SlashCommand } from "../types/command-types";

export const useCommandSuggestions = (input: string) => {
  const { commandHistory } = useSlashCommandStore();
  const { parseInput, findMatchingCommands } = useCommandParser();

  // Dynamic suggestions state
  const [dynamicSuggestions, setDynamicSuggestions] = useState<SlashCommand[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse input - memoized to prevent unnecessary recalculations
  const parseResult = useMemo(() => {
    if (!input)
      return { isCommand: false, commandPath: [], query: "", rawInput: "" };
    return parseInput(input);
  }, [input, parseInput]);
  const { isCommand, commandPath, query } = parseResult;

  // Static suggestions (from command registry)
  const staticSuggestions = useMemo(() => {
    if (!isCommand) return [];

    // Get matching commands from parser
    const matches = findMatchingCommands(input);

    // Filter out low score matches (sadece yüksek score'lu command'ları göster)
    const filteredMatches = matches.filter((m) => m.matchScore >= 40);

    return filteredMatches.map((m) => m.command);
  }, [isCommand, input, findMatchingCommands]);

  // Dynamic content loading
  // commandPath ve query'yi string'e çevirerek dependency array'i stabilize et
  // Bu sayede array referans değişiklikleri loop'a sebep olmaz
  const commandPathString = useMemo(
    () => JSON.stringify(commandPath),
    [commandPath]
  );
  const queryString = useMemo(() => query || "", [query]);

  useEffect(() => {
    // Debounce için timeout
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;

    const loadDynamicSuggestions = async () => {
      // Parse commandPath from string (her effect'te fresh parse)
      let currentCommandPath: string[] = [];
      try {
        currentCommandPath = JSON.parse(commandPathString) as string[];
      } catch {
        currentCommandPath = [];
      }

      if (!isCommand || currentCommandPath.length === 0) {
        // Reset dynamic suggestions if not a command
        if (!isCancelled) {
          setDynamicSuggestions([]);
          setError(null);
        }
        return;
      }

      // Get active command (last in path)
      const activeCommand = findCommandByPath(currentCommandPath);

      // Check if it has dynamic loader
      if (activeCommand?.dynamicLoader) {
        if (!isCancelled) {
          setIsLoading(true);
          setError(null);
        }

        try {
          // Load dynamic content
          const results = await activeCommand.dynamicLoader(queryString);

          // Component unmount olmuşsa veya iptal edilmişse state güncelleme
          if (!isCancelled) {
            setDynamicSuggestions(results);
            setIsLoading(false);
                }
              } catch (err) {
                if (!isCancelled) {
                  setError(
                    err instanceof Error ? err.message : "Failed to load suggestions"
                  );
                  setIsLoading(false);
                }
              }
      } else {
        // Dynamic loader yoksa suggestions'ı temizle
        if (!isCancelled) {
          setDynamicSuggestions([]);
          setError(null);
        }
      }
    };

    // Debounce: 300ms bekle (query değişikliklerinde)
    timeoutId = setTimeout(() => {
      loadDynamicSuggestions();
    }, 300);

    // Cleanup
    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isCommand, commandPathString, queryString]); // Sadece stabilize edilmiş dependency'ler - currentCommandPath gereksiz

  // Merge static + dynamic suggestions
  const allSuggestions = useMemo(() => {
    return [...staticSuggestions, ...dynamicSuggestions];
  }, [staticSuggestions, dynamicSuggestions]);

  // Rank suggestions by history
  const rankedSuggestions = useMemo(() => {
    return allSuggestions.sort((a, b) => {
      // Find in history
      const aHistory = commandHistory.find((h) => h.id === a.id);
      const bHistory = commandHistory.find((h) => h.id === b.id);

      // Both in history - sort by usage count and timestamp
      if (aHistory && bHistory) {
        // More usage = higher priority
        if (aHistory.usageCount !== bHistory.usageCount) {
          return bHistory.usageCount - aHistory.usageCount;
        }
        // Same usage count - more recent = higher priority
        return bHistory.timestamp - aHistory.timestamp;
      }

      // Only A in history
      if (aHistory && !bHistory) return -1;

      // Only B in history
      if (!aHistory && bHistory) return 1;

      // Neither in history - keep original order
      return 0;
    });
  }, [allSuggestions, commandHistory]);

  // Filter out duplicates (by ID)
  const uniqueSuggestions = useMemo(() => {
    const seen = new Set<string>();
    return rankedSuggestions.filter((suggestion) => {
      if (seen.has(suggestion.id)) {
        return false;
      }
      seen.add(suggestion.id);
      return true;
    });
  }, [rankedSuggestions]);

  return {
    suggestions: uniqueSuggestions,
    isLoading,
    error,
    hasResults: uniqueSuggestions.length > 0,
    parseResult,
  };
};
